/*
 * Copyright (c) 2016-2018. campoint AG.
 * This file is licensed under the MIT License (https://opensource.org/licenses/MIT).
 */

import { supportsWebSockets } from './capabilities';
import { logger } from './logging/logger';
import Response from './response';

interface QEntry {
    action: string;
    params: any;
    callback: (response: Response) => any;
    format?: string;
    queued?: boolean;
    timeoutId?: ReturnType<typeof setTimeout>;
}

const MAX_TIMEOUT = 60000;
const commandTimeout = {
    CMDP_CCHARGE: 20000,
    CMDP_CLOGOUT: 4000,
    CMDP_CQUERY_CHARGE: 20000,
    CMDP_NOOP: 30000,
};

function timeoutByAction(entry: QEntry): number {
    return commandTimeout[entry.action] || 8000;
}

function noop(): void {
    /* noop function */
}

function fetchJsonp(url: string, timeout: number, onComplete, onError): void {
    // eslint-disable-next-line prefer-const
    let timeoutId: ReturnType<typeof setTimeout>;
    const callbackFn =
        '_jsonp_' + Date.now() + '_' + Math.ceil(Math.random() * 0x10000).toString(16);
    const jsonpScript = document.createElement('script');
    const parent = document.querySelectorAll('head')[0];

    const cleanup = (): void => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        jsonpScript.remove();
        try {
            delete window[callbackFn];
        } catch {
            window[callbackFn] = undefined;
        }
    };

    window[callbackFn] = (response): void => {
        cleanup();
        onComplete(response);
    };

    jsonpScript.src = url + (url.includes('?') ? '&' : '?') + 'callback=' + callbackFn;
    jsonpScript.onerror = (): void => {
        cleanup();
        onError('error');
    };

    parent.append(jsonpScript);

    timeoutId = setTimeout(() => {
        cleanup();
        window[callbackFn] = noop;
        onError('timeout');
    }, timeout);
}

export default class Connection {
    private clientId: string;
    private host: string;
    private servletUrl: string;
    private queue: QEntry[] = [];
    private counter = 1;
    private socket: WebSocket;
    private commandsHandler: (r: Response) => void;
    private noopActive = false;
    private processedCommands: string[] = [];
    private ackCommands: string[] = [];
    private lastNoop = 0;
    private useWS: boolean;
    private instanceId: string;
    private closing = false;

    constructor(clientId: string, host: string, servletUrl: string, forceLongPooling: boolean) {
        this.clientId = clientId;
        this.host = host;
        this.servletUrl = servletUrl;
        this.instanceId = Math.ceil(Math.random() * 0x1000000).toString(16);
        while (this.instanceId.length < 6) {
            this.instanceId = '0' + this.instanceId;
        }

        this.useWS = supportsWebSockets && !forceLongPooling;
        if (this.useWS) {
            this.openSocket();
        }
    }

    public send(action: string, params: any, callback: (response: Response) => any): void {
        params.clientID = this.clientId;
        params._uniq = this.counter++;
        params._iid = this.instanceId;

        logger.log('Connection.send', action, '_uniq', params._uniq);

        const entry: QEntry = {
            action: action,
            params: params,
            callback: callback,
            queued: false,
            timeoutId: undefined,
            // format: 'json',
            // strip: true,
        };

        if (this.useWS) {
            this.queue.push(entry);
            if (this.socket.readyState === WebSocket.OPEN) {
                this.sendOverSocket(entry);
            } else {
                entry.queued = true;
            }
        } else {
            this.sendOverJsonp(entry);
        }
    }

    public startNoop(noopCommandsHandler: (r: Response) => void): void {
        this.noopActive = true;
        this.commandsHandler = noopCommandsHandler;
        this.sendNoop('');
    }

    public close(): void {
        this.closing = true;
        this.noopActive = false;
        this.queue.forEach((element) => {
            if (element.timeoutId) {
                window.clearTimeout(element.timeoutId);
            }
        });
        // empty queue.
        this.queue.splice(0, this.queue.length);
        if (this.socket) {
            this.socket.close();
        }
    }

    private openSocket(): void {
        const url = `wss://${this.host}/VXControl/`;
        logger.log('Connection.openSocket', url);
        let socket: WebSocket;
        try {
            socket = new WebSocket(url);
        } catch (error) {
            // @MDN SECURITY_ERR The port to which the connection is being attempted is being blocked.
            logger.warn('Connection.openSocket SECURITY_ERR', error);
            this.useWS = false;
            return;
        }

        socket.onopen = (event): void => {
            logger.log('Connection:socket.onopen', event);

            this.queue.forEach((entry) => {
                if (entry.queued) {
                    entry.queued = false;
                    this.sendOverSocket(entry);
                }
            });
        };

        socket.onmessage = (event): void => {
            logger.log('Connection:socket.onmessage', event);

            const data = JSON.parse(event.data);
            if (data.action) {
                const cmdId = data.params ? data.params._uniq : -1;
                for (let i = 0, l = this.queue.length; i < l; i++) {
                    const entry = this.queue[i];
                    if (entry.params._uniq == cmdId) {
                        clearTimeout(entry.timeoutId);
                        this.queue.splice(i, 1);
                        entry.callback(Response.parse(data.response));
                        break;
                    }
                }
            } else {
                const response = Response.parse(data.command);
                this.handleNoopResponse(response);
            }
        };

        socket.onerror = (errorEvent): void => {
            logger.warn('Connection:socket.onerror', errorEvent);
            this.fallback();
        };

        socket.onclose = (event): void => {
            logger.log('Connection:socket.onclose', event);
            if (!this.closing && this.useWS) {
                this.fallback();
            }
        };
        this.socket = socket;
    }

    private fallback(): void {
        this.useWS = false;
        this.queue.forEach((entry) => {
            if (entry.queued) {
                entry.queued = false;
                this.sendOverJsonp(entry);
            }
        });
    }

    private sendNoop(commandIds: string): void {
        if (this.lastNoop === 0) {
            this.lastNoop = Date.now();
        }
        const params = { commandIDs: commandIds };
        this.send('CMDP_NOOP', params, this.handleNoopResponse.bind(this));
    }

    private handleNoopResponse(response: Response): void {
        if (!response.ok || response.commands.length > 0) {
            logger.log('noopResponseHandler', response);
        }

        if (response.ok) {
            this.lastNoop = 0;
            this.ackCommands = [];
            for (let i = response.commands.length - 1; i >= 0; i--) {
                const cmdId: string = response.commands[i].id;
                this.ackCommands.push(cmdId);
                // prevents that commands are being processed multiple times
                if (cmdId === undefined) {
                    logger.warn('Received command without id');
                } else if (this.processedCommands.includes(cmdId)) {
                    logger.warn('remove processed command', cmdId);
                    response.commands.splice(i, 1);
                } else {
                    this.processedCommands.push(cmdId);
                }
            }

            while (this.processedCommands.length > 50) {
                this.processedCommands.shift();
            }
        }

        if (
            (response.commands.length > 0 || !response.ok) &&
            (response !== Response.TIMEOUT || Date.now() - this.lastNoop > MAX_TIMEOUT)
        ) {
            this.commandsHandler(response);
        }

        if (this.noopActive) {
            this.sendNoop(this.ackCommands.toString());
        }
    }

    private sendOverSocket(entry: QEntry): void {
        entry.timeoutId = setTimeout(() => {
            window.clearTimeout(entry.timeoutId);
            const index = this.queue.indexOf(entry);
            if (index >= 0) {
                this.queue.splice(index, 1);
            }
            if (this.noopActive || entry.action !== 'CMDP_NOOP') {
                entry.callback(Response.TIMEOUT);
            }
        }, timeoutByAction(entry));

        this.socket.send(JSON.stringify({ action: entry.action, params: entry.params }));
    }

    private sendOverJsonp(entry: QEntry): void {
        let url = `${this.servletUrl}?action=${entry.action}`;
        for (const key in entry.params) {
            if (Object.prototype.hasOwnProperty.call(entry.params, key)) {
                url += '&' + encodeURIComponent(key) + '=' + encodeURIComponent(entry.params[key]);
            }
        }

        fetchJsonp(
            url,
            timeoutByAction(entry),
            (response: string) => {
                entry.callback(Response.parse(response));
            },
            (error: string) => {
                logger.warn('sendOverJsonp error', error, url);
                if (error == 'timeout') {
                    entry.callback(Response.TIMEOUT);
                } else {
                    entry.callback(Response.NETWORK_ERROR);
                }
            }
        );
    }
}
